import React, { useState, useEffect } from 'react';
import StrategyHeader from './StrategyHeader';
import AnalyticsBlock from './AnalyticsBlock';
import { fmtIST, isTodayIST } from './dashboardUtils';
import PriceProfile from './PriceProfile';

const SwingScanActive = () => {
  const [status, setStatus] = useState(null);
  const [openPositions, setOpenPositions] = useState([]);
  const [closedPositions, setClosedPositions] = useState([]);
  const [lifetime, setLifetime] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchAll = async () => {
    try {
      const [s, op, cp, lt] = await Promise.all([
        fetch('/swingscan/status').then(r => r.json()),
        fetch('/swingscan/positions?status=OPEN').then(r => r.json()),
        fetch('/swingscan/positions?status=CLOSED&limit=30').then(r => r.json()),
        fetch('/swingscan/lifetime').then(r => r.json())
      ]);
      setStatus(s);
      setOpenPositions(op.positions || []);
      setClosedPositions(cp.positions || []);
      setLifetime(lt);

      const positions = op.positions || [];
      if (positions.length > 0) {
        const tickResults = await Promise.all(
          positions.map(p =>
            fetch(`/tick/state/${p.ticker}`)
              .then(r => r.ok ? r.json() : null)
              .catch(() => null)
          )
        );
        const priceMap = {};
        positions.forEach((p, i) => {
          const tick = tickResults[i];
          if (tick && tick.state && tick.state.latest_ltp != null) {
            priceMap[p.ticker] = {
              ltp: tick.state.latest_ltp,
              ts: null,
              stream_healthy: tick.state?.stream_healthy,
              rsi: tick.state?.rsi_now,
              macd_hist: tick.state?.macd_hist_now,
              price_vs_vwap: tick.state?.price_vs_vwap_pct,
            };
          }
        });
        setLivePrices(priceMap);
      }
    } catch (err) {
      console.error('SwingScan fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
  }, []);

  const fmtRs = (v) => v == null ? '—' :
    (v >= 0 ? '+' : '') + '₹' + Math.round(v).toLocaleString('en-IN');

  const fmtRsPlain = (v) => v == null ? '—' :
    '₹' + Math.round(v).toLocaleString('en-IN');

  const fmtPct = (v) => v == null ? '—' : (v >= 0 ? '+' : '') + v.toFixed(2) + '%';

  const getPnLColor = (v) => v == null ? '#666' : v > 0 ? '#0a7' : v < 0 ? '#c33' : '#666';

  const CondPill = ({ label, met }) => (
    <span style={{
      display: 'inline-block',
      background: met ? '#f6ffed' : '#fff1f0',
      color: met ? '#389e0d' : '#cf1322',
      border: `1px solid ${met ? '#b7eb8f' : '#ffa39e'}`,
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      margin: '2px 4px 2px 0',
      fontWeight: 500
    }}>
      {met ? '✓' : '✗'} {label}
    </span>
  );

  if (loading) return <div style={{padding: 20}}>Loading SwingScan Active...</div>;

  const PATH_A_KEYS = ['volatility_settled', 'was_oversold_recently', 'recovering', 'macd_hist_flipped_positive'];
  const PATH_B_KEYS = ['base_compressed', 'volume_expanding', 'breakout_15bar', 'rsi_early_momentum', 'macd_rising', 'above_vwap', 'not_too_extended'];
  const PATH_C_KEYS = ['pathC_breakout_15bar', 'pathC_volume_3x_20d', 'pathC_close_upper_60_range', 'pathC_chg_5d_in_range'];
  // v1.1.5 Dashboard: Path label remap for display
const remapPath = (firedPath) => {
  if (!firedPath) return '';
  if (firedPath.startsWith('INTEL')) return 'INTEL';
  if (firedPath === 'A') return 'PULLBACK';
  if (firedPath === 'B') return 'BREAKOUT';
  if (firedPath === 'C') return 'HYBRID';
  return firedPath;
};

const isIntelTrade = (firedPath) => firedPath && firedPath.startsWith('INTEL');

const BASELINE_KEYS = ['nifty_ok', 'multi_day_trend', 'ema_stack'];

  return (
    <div style={{padding: 20, fontFamily: 'system-ui'}}>
      <div style={{background: '#e6f7ff', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #91d5ff'}}>
        <div style={{fontWeight: 600, color: '#0050b3'}}>SwingScan Active v1.0</div>
        <div style={{fontSize: 13, color: '#555', marginTop: 4}}>
          Intelligence Framework v1.1.12 on 669 Trendlyne tickers · Rs1L/position · 18 slots (13 momentum, 2 DIP, 3 reserved rank 1-5) · BE/Half-book/Trail exits, no T2 · 4-day time cap · Paper mode
        </div>
      </div>

      <StrategyHeader
        strategyKey="swingscan"
        lifetime={lifetime}
        openPositions={openPositions}
        todayClosed={closedPositions.filter(p => isTodayIST(p.exit_ts))}
      />

      <AnalyticsBlock strategyKey="swingscan" />

      <div style={{background: '#fff', padding: 16, border: '1px solid #ddd', borderRadius: 8, marginBottom: 16}}>
        <div style={{fontWeight: 600, marginBottom: 12}}>
          Open Positions ({openPositions.length})
          <span style={{fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 12}}>
            Click ▶ ticker to expand conditions fired at entry
          </span>
        </div>
        {openPositions.length === 0 ? (
          <div style={{color: '#888', fontSize: 13}}>No open positions</div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', fontSize: 12, borderCollapse: 'collapse'}}>
              <thead style={{background: '#fafafa'}}>
                <tr>
                  <th style={{padding: 6, textAlign: 'left'}}>Ticker</th>
                  <th style={{padding: 6, textAlign: 'right'}}>Entry</th>
                  <th style={{padding: 6, textAlign: 'right'}}>LTP</th>
                  <th style={{padding: 6, textAlign: 'right'}}>Qty</th>
                  <th style={{padding: 6, textAlign: 'right'}}>Value</th>
                  <th style={{padding: 6, textAlign: 'right'}}>SL</th>
                  <th style={{padding: 6, textAlign: 'right'}}>SL Dist</th>
                  <th style={{padding: 6, textAlign: 'right'}}>Risk</th>
                  <th style={{padding: 6, textAlign: 'right'}}>Unrealized</th>
                  <th style={{padding: 6, textAlign: 'right'}}>P&L/Risk</th>
                  <th style={{padding: 6, textAlign: 'right'}}>MFE%</th>
                  <th style={{padding: 6, textAlign: 'left'}}>Stage</th>
                  <th style={{padding: 6, textAlign: 'left'}}>Conv</th>
                  <th style={{padding: 6, textAlign: 'left'}}>Path</th>
                  <th style={{padding: 6, textAlign: 'left'}}>Entered</th>
                </tr>
              </thead>
              <tbody>
                {openPositions.map(p => {
                  const live = livePrices[p.ticker];
                  const ltp = live?.ltp;
                  const entry = Number(p.entry_price);
                  const qty = Number(p.qty);
                  const sl = Number(p.stop_loss);
                  const value = ltp ? ltp * qty : null;
                  const gross = ltp ? (ltp - entry) * qty : null;
                  const cost = entry * qty * 0.0012;
                  const netPnl = gross != null ? gross - cost : null;
                  const netPct = ltp ? ((ltp - entry) / entry) * 100 - 0.12 : null;
                  const risk = (entry - sl) * qty;
                  const pnlRisk = netPnl != null && risk > 0 ? netPnl / risk : null;
                  const slDistPct = ltp ? ((ltp - sl) / ltp) * 100 : null;
                  const isExpanded = expandedRow === p.id;
                  const conds = p.entry_conditions_met || {};
                  const isSlHealthy = live?.stream_healthy;

                  return (
                    <React.Fragment key={p.id}>
                      <tr style={{borderTop: '1px solid #eee', cursor: 'pointer'}}
                          onClick={() => setExpandedRow(isExpanded ? null : p.id)}>
                        <td style={{padding: 6, fontWeight: 600}}>
                          <span style={{color: '#888', marginRight: 4}}>{isExpanded ? '▼' : '▶'}</span>
                          {p.ticker}
                          {p.setup_type === 'DIP' && (
                            <span title="DIP setup: strong stock, 2nd red day" style={{
                              marginLeft: 6, background: '#fff7e6', color: '#ad6800',
                              padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700
                            }}>DIP</span>
                          )}
                          {!isSlHealthy && <span title="Tick stream unhealthy" style={{color: '#c33', marginLeft: 4}}>⚠</span>}
                        </td>
                        <td style={{padding: 6, textAlign: 'right'}}>₹{entry.toFixed(2)}</td>
                        <td style={{padding: 6, textAlign: 'right', fontWeight: 600, color: ltp && ltp > entry ? '#0a7' : ltp && ltp < entry ? '#c33' : '#333'}}>
                          {ltp ? `₹${ltp.toFixed(2)}` : '—'}
                        </td>
                        <td style={{padding: 6, textAlign: 'right'}}>{qty}</td>
                        <td style={{padding: 6, textAlign: 'right'}}>{fmtRsPlain(value)}</td>
                        <td style={{padding: 6, textAlign: 'right'}}>₹{sl.toFixed(2)}</td>
                        <td style={{padding: 6, textAlign: 'right', fontSize: 11, color: '#666'}}>
                          {slDistPct != null ? slDistPct.toFixed(2) + '%' : '—'}
                        </td>
                        <td style={{padding: 6, textAlign: 'right', color: '#c33'}}>₹{Math.round(risk).toLocaleString('en-IN')}</td>
                        <td style={{padding: 6, textAlign: 'right', color: getPnLColor(netPnl), fontWeight: 600}}>
                          {netPnl != null ? `${fmtRs(netPnl)} (${fmtPct(netPct)})` : '—'}
                        </td>
                        <td style={{padding: 6, textAlign: 'right', color: getPnLColor(pnlRisk), fontWeight: 600}}>
                          {pnlRisk != null ? (pnlRisk >= 0 ? '+' : '') + pnlRisk.toFixed(2) + 'x' : '—'}
                        </td>
                        <td style={{padding: 6, textAlign: 'right'}}>{fmtPct(p.mfe_pct)}</td>
                        <td style={{padding: 6, fontSize: 11}}>{p.current_stage}</td>
                        <td style={{padding: 6}}>
                          <span style={{
                            background: p.conviction === 'HIGH' ? '#f6ffed' : '#fffbe6',
                            color: p.conviction === 'HIGH' ? '#389e0d' : '#ad8b00',
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600
                          }}>
                            {p.conviction}
                          </span>
                        </td>
                        <td style={{padding: 6, fontSize: 11}}>{remapPath(p.fired_path)}</td>
                        <td style={{padding: 6, fontSize: 11, color: '#666'}}>
                          {p.entry_ts ? new Date(p.entry_ts).toLocaleString('en-IN', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : '—'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={15} style={{padding: 12, background: '#fafbfc', borderTop: '1px solid #eee'}}>
                            <div style={{display: 'flex', gap: 32, alignItems: 'flex-start'}}>
                              <div style={{minWidth: 260}}>
                                <PriceProfile ticker={p.ticker} ltp={ltp} setupType={p.setup_type} />
                                {p.setup_reason && (
                                  <div style={{marginTop: 10, fontSize: 11, color: '#ad6800',
                                               background: '#fff7e6', padding: '6px 8px', borderRadius: 4}}>
                                    {p.setup_reason}
                                  </div>
                                )}
                              </div>
                              <div style={{flex: 1}}>
                            {!isIntelTrade(p.fired_path) && (
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, fontSize: 12}}>
                              <div>
                                <div style={{fontWeight: 600, color: '#333', marginBottom: 6}}>Baseline (all required)</div>
                                {BASELINE_KEYS.map(k => (
                                  <CondPill key={k} label={k} met={conds[k]} />
                                ))}
                              </div>
                              <div>
                                <div style={{fontWeight: 600, color: '#333', marginBottom: 6}}>
                                  Path A — pullback recovery
                                  <span style={{marginLeft: 8, fontSize: 11, color: conds._pathA_met ? '#389e0d' : '#c33'}}>
                                    ({conds._pathA_met ? 'FIRED' : 'not fired'})
                                  </span>
                                </div>
                                {PATH_A_KEYS.map(k => (
                                  <CondPill key={k} label={k} met={conds[k]} />
                                ))}
                              </div>
                              <div>
                                <div style={{fontWeight: 600, color: '#333', marginBottom: 6}}>
                                  Path B — early climb
                                  <span style={{marginLeft: 8, fontSize: 11, color: conds._pathB_met ? '#389e0d' : '#c33'}}>
                                    ({conds._pathB_met ? 'FIRED' : 'not fired'})
                                  </span>
                                </div>
                                {PATH_B_KEYS.map(k => (
                                  <CondPill key={k} label={k} met={conds[k]} />
                                ))}
                              </div>
                              <div>
                                <div style={{fontWeight: 600, color: '#333', marginBottom: 6}}>
                                  Path C — momentum continuation
                                  <span style={{marginLeft: 8, fontSize: 11, color: conds._pathC_met ? '#389e0d' : '#c33'}}>
                                    ({conds._pathC_met ? 'FIRED' : 'not fired'})
                                  </span>
                                </div>
                                {PATH_C_KEYS.map(k => (
                                  <CondPill key={k} label={k.replace('pathC_', '')} met={conds[k]} />
                                ))}
                              </div>
                            </div>
                            )}

                            {isIntelTrade(p.fired_path) && (
                            <div style={{padding: 12, background: '#eff6ff', borderRadius: 6, fontSize: 12}}>
                              <div style={{fontWeight: 600, color: '#1e40af', marginBottom: 8}}>
                                Framework Attribution
                              </div>
                              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                                <div>
                                  <div><b>Ladder tier:</b> {p.ladder_tier || conds._fired_path?.replace('INTEL_', '')}</div>
                                  <div><b>Net conviction:</b> {conds._conviction || `NET_${p.net_conviction}`}</div>
                                  <div><b>Rank in GO:</b> {conds._intel_rank ? `#${conds._intel_rank}` : '-'}</div>
                                  <div><b>Sector:</b> {conds._sector || '-'}</div>
                                </div>
                                <div>
                                  <div><b>Framework version:</b> {conds._framework_version || 'v1.1'}</div>
                                  <div><b>Earnings action:</b> {conds._earnings_action || 'FULL_SIZE'}</div>
                                  <div><b>Days to earnings:</b> {conds._days_to_earnings ?? '-'}</div>
                                  <div><b>Intel row ID:</b> {conds._intel_id || '-'}</div>
                                </div>
                              </div>
                              {p.entry_check_verdict && (
                                <div style={{marginTop: 10, paddingTop: 10, borderTop: '1px solid #dbeafe'}}>
                                  <div style={{fontWeight: 600, marginBottom: 4}}>Entry Intelligence Check</div>
                                  <span style={{marginRight: 12}}>Verdict: <b style={{color: p.entry_check_verdict === 'PROCEED' ? '#059669' : '#d97706'}}>{p.entry_check_verdict}</b></span>
                                  {p.entry_check_score !== null && <span style={{marginRight: 12}}>Score: <b>{p.entry_check_score >= 0 ? '+' : ''}{p.entry_check_score}</b></span>}
                                  {p.entry_check_hard_gate && <span style={{marginRight: 12}}>Gate: <b>{p.entry_check_hard_gate}</b></span>}
                                  {p.entry_check_day_change_pct !== null && <span>Day change: <b>{p.entry_check_day_change_pct >= 0 ? '+' : ''}{p.entry_check_day_change_pct}%</b></span>}
                                </div>
                              )}
                            </div>
                            )}

                            {live && (
                              <div style={{marginTop: 12, padding: 8, background: '#f0f5ff', borderRadius: 4, fontSize: 11}}>
                                <div style={{fontWeight: 600, marginBottom: 4}}>Live tick state (as of {live.ts ? new Date(live.ts).toLocaleTimeString('en-IN') : '—'})</div>
                                <span style={{marginRight: 16}}>RSI: <b>{live.rsi?.toFixed(2)}</b></span>
                                <span style={{marginRight: 16}}>MACD hist: <b>{live.macd_hist?.toFixed(4)}</b></span>
                                <span style={{marginRight: 16}}>Price vs VWAP: <b>{live.price_vs_vwap?.toFixed(3)}%</b></span>
                                <span style={{marginRight: 16}}>Stream: <b style={{color: live.stream_healthy ? '#389e0d' : '#c33'}}>{live.stream_healthy ? 'HEALTHY' : 'STALE'}</b></span>
                              </div>
                            )}

                            <div style={{marginTop: 8, fontSize: 11, color: '#666'}}>
                              <b>Entry reason:</b> {p.entry_reason}
                            </div>
                            <div style={{marginTop: 4, fontSize: 11, color: '#888'}}>
                              Paper mode — SL managed in-code (no Kite SL order placed). Position ID: {p.id}
                            </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{background: '#fff', padding: 16, border: '1px solid #ddd', borderRadius: 8}}>
        {(() => {
          const todayClosed = closedPositions.filter(p => isTodayIST(p.exit_ts));
          const historyClosed = closedPositions.filter(p => !isTodayIST(p.exit_ts));
          const renderRow = (p) => (
            <tr key={p.id} style={{borderTop: '1px solid #eee'}}>
              <td style={{padding: 8, fontWeight: 600}}>{p.ticker}</td>
              <td style={{padding: 8, textAlign: 'right'}}>₹{p.entry_price?.toFixed(2)}</td>
              <td style={{padding: 8, textAlign: 'right'}}>₹{p.exit_price?.toFixed(2)}</td>
              <td style={{padding: 8, fontSize: 11}}>{p.exit_reason}</td>
              <td style={{padding: 8, fontSize: 11}}>{p.conviction}/{remapPath(p.fired_path)}</td>
              <td style={{padding: 8, textAlign: 'right', color: getPnLColor(p.net_pnl), fontWeight: 600}}>
                {fmtRs(p.net_pnl)}
              </td>
              <td style={{padding: 8, textAlign: 'right'}}>{fmtPct(p.mfe_pct)}</td>
              <td style={{padding: 8, fontSize: 10, color: '#888', whiteSpace: 'nowrap'}}>{fmtIST(p.entry_ts)}</td>
              <td style={{padding: 8, fontSize: 10, color: '#888', whiteSpace: 'nowrap'}}>{fmtIST(p.exit_ts)}</td>
            </tr>
          );
          const renderTable = (rows) => (
            <div style={{overflowX: 'auto', marginBottom: 12}}>
              <table style={{width: '100%', fontSize: 13, borderCollapse: 'collapse'}}>
                <thead style={{background: '#fafafa'}}>
                  <tr>
                    <th style={{padding: 8, textAlign: 'left'}}>Ticker</th>
                    <th style={{padding: 8, textAlign: 'right'}}>Entry</th>
                    <th style={{padding: 8, textAlign: 'right'}}>Exit</th>
                    <th style={{padding: 8, textAlign: 'left'}}>Reason</th>
                    <th style={{padding: 8, textAlign: 'left'}}>Conv/Path</th>
                    <th style={{padding: 8, textAlign: 'right'}}>Net P&L</th>
                    <th style={{padding: 8, textAlign: 'right'}}>MFE%</th>
                    <th style={{padding: 8, textAlign: 'left'}}>Entered</th>
                    <th style={{padding: 8, textAlign: 'left'}}>Exited</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(renderRow)}
                </tbody>
              </table>
            </div>
          );
          return (
            <>
              <div style={{fontWeight: 600, marginBottom: 12}}>
                Closed Today ({todayClosed.length})
              </div>
              {todayClosed.length === 0 ? (
                <div style={{color: '#888', fontSize: 13, marginBottom: 16}}>No positions closed today</div>
              ) : renderTable(todayClosed)}
              {historyClosed.length > 0 && (
                <details style={{marginTop: 12}}>
                  <summary style={{cursor: 'pointer', fontWeight: 600, color: '#0050b3', padding: 4}}>
                    ▶ History ({historyClosed.length} older closed) — click to expand
                  </summary>
                  <div style={{marginTop: 8}}>
                    {renderTable(historyClosed)}
                  </div>
                </details>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default SwingScanActive;
